import type {
  FilterBooleanOperator,
  FilterElementInput,
  FilterValueInput,
  FilterBooleanOperationInput,
} from "app/views/cloudops/resource-matches/__generated__/ResourceMatchesTableRefetchQuery.graphql";

// Re-export so we don't have to arbitrarily import these types from generated code
export type {
  FilterBooleanOperator,
  FilterElementInput,
  FilterValueInput,
  FilterBooleanOperationInput,
};

/**
 * Creates a single filter element
 *
 * @param name - The name of the filter field
 * @param value - The value to filter by
 * @param operator - Optional operator (e.g., 'equals', 'contains')
 * @returns A FilterElementInput with a single filter, or undefined if value is undefined
 */
export function createSingleFilter(
  name: string,
  value: string | undefined,
  operator?: string,
): FilterElementInput | undefined {
  if (value === undefined) {
    return undefined;
  }

  return {
    single: {
      name,
      value,
      ...(operator ? { operator } : {}),
    },
  };
}

/**
 * Creates a multiple filter element with the given operator and operands
 * Automatically filters out any undefined operands
 *
 * @param operands - Array of filter elements (undefined values will be filtered out)
 * @param operator - The boolean operator to combine filters with ('AND' or 'OR')
 * @returns A FilterElementInput with multiple filters, or undefined if no valid operands
 */
export function createMultipleFilter(
  operands: (FilterElementInput | undefined)[],
  operator: FilterBooleanOperator = "AND",
): FilterElementInput | undefined {
  const validOperands = operands.filter(
    (operand): operand is FilterElementInput => operand !== undefined,
  );

  if (validOperands.length === 0) {
    return undefined;
  }

  if (validOperands.length === 1) {
    return validOperands[0];
  }

  return {
    multiple: {
      operator,
      operands: validOperands,
    },
  };
}

/**
 * Creates a filter element from an array of filters
 * Automatically decides whether to use single or multiple based on the number of filters
 * Filters out any undefined values automatically
 *
 * @param filters - Array of filters to combine
 * @param operator - The boolean operator to combine filters with ('AND' or 'OR')
 * @returns A FilterElementInput
 */
export function createFilterElement(
  filters: { name: string; value: string | undefined; operator?: string }[],
  operator: FilterBooleanOperator = "AND",
): FilterElementInput | null {
  // Filter out entries with undefined values
  const validFilters = filters.filter((filter) => filter.value !== undefined);

  if (validFilters.length === 0) {
    return null;
  }

  if (validFilters.length === 1) {
    const singleFilter = createSingleFilter(
      validFilters[0].name,
      validFilters[0].value,
      validFilters[0].operator,
    );
    return singleFilter || null;
  }

  const operands = validFilters
    .map((filter) =>
      createSingleFilter(filter.name, filter.value, filter.operator),
    )
    .filter((operand): operand is FilterElementInput => operand !== undefined);

  if (operands.length === 0) {
    return null;
  }

  const result = createMultipleFilter(operands, operator);
  return result || null;
}
